import { useReactiveVar } from "@apollo/client";
import { userDetailsVar } from "../../lib/cache";
import { Box, Button, ButtonGroup, Flex, VStack } from "@chakra-ui/react";
import { SelectFieldV2 } from "../../components/selectField/SelectFieldV2";
import {
  bsdLookbackOptionsType,
  removeUnderscores,
  TempBayerAdvertiserChoicesEnum,
  TempAppleAdvertiserChoicesEnum,
  BsdLookbackOptionsEnum,
  getBsdLookbackName,
  getAppleAdvertiserName,
  getBayerAdvertiserName,
  pageTypeOptionsType,
  unCamelcase,
  PageTypeEnum,
  TempDemoAdvertiserChoicesEnum,
  getDemoAdvertiserName,
} from "../../utils/displayGetters";
import {
  BreakdownTypeEnum,
  TacticIdTypeEnum,
  BrandChoicesEnum,
} from "../../graphqlExternal/generated";
import { useQuery } from "@apollo/client";
import { GET_CAMPAIGN_IDS } from "../../graphqlExternal/operations/operations";
import React, { useState } from "react";
import { BrandChoicesOptionsType } from "./index";

interface DashboardFilterProps {
  localBrandFilter: BrandChoicesOptionsType | undefined;
  setLocalBrandFilter: React.Dispatch<
    React.SetStateAction<BrandChoicesOptionsType | undefined>
  >;
  breakdownType: BreakdownTypeEnum;
  setBreakdownType: React.Dispatch<React.SetStateAction<BreakdownTypeEnum>>;
  localPageTypeFilter: pageTypeOptionsType[] | undefined;
  setLocalPageTypeFilter: React.Dispatch<
    React.SetStateAction<pageTypeOptionsType[] | undefined>
  >;
  localDateRangeFilter: bsdLookbackOptionsType;
  setLocalDateRangeFilter: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }>
  >;
  localAdvertiserFilter: { label: string; value: string }[] | undefined;
  setLocalAdvertiserFilter: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }[] | undefined>
  >;
  localCampaignFilter: { label: string; value: string }[] | undefined;
  setLocalCampaignFilter: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }[] | undefined>
  >;
  localLineItemFilter: { label: string; value: string }[] | undefined;
  setLocalLineItemFilter: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }[] | undefined>
  >;
  startDateString: string;
  endDateString: string;
}

export const DashboardFilter = ({
  breakdownType,
  localBrandFilter,
  setLocalBrandFilter,
  setBreakdownType,
  localPageTypeFilter,
  setLocalPageTypeFilter,
  localDateRangeFilter,
  setLocalDateRangeFilter,
  localAdvertiserFilter,
  setLocalAdvertiserFilter,
  localCampaignFilter,
  setLocalCampaignFilter,
  localLineItemFilter,
  setLocalLineItemFilter,
  startDateString,
  endDateString,
}: DashboardFilterProps) => {
  const currentUser = useReactiveVar(userDetailsVar);
  const keywordSelectedSliderStart = "55%";
  const [currentPageTypeSelection, setCurrentPageTypeSelection] = useState<pageTypeOptionsType[]>();
  const [currentAdvertiserSelection, setCurrentAdvertiserSelection] =
    useState<{ label: string; value: string }[]>();
  const [currentCampaignIdSelection, setCurrentCampaignIdSelection] =
    useState<{ label: string; value: string }[]>();
  const [currentLineItemIdSelection, setCurrentLineItemIdSelection] =
    useState<{ label: string; value: string }[]>();


  let lookbackOptions: bsdLookbackOptionsType[] = [];

  lookbackOptions = [
    ...Object.entries(BsdLookbackOptionsEnum).map(([, value]) => ({
      label: getBsdLookbackName(value),
      value: value,
    })),
  ];

  const advertiserOptions: { label: string; value: string }[] | undefined =
    determineAdvertiserChoices(localBrandFilter);

  const brandOptions: { label: string; value: BrandChoicesEnum }[] = [
    ...Object.entries(BrandChoicesEnum).map(([, value]) => ({
      label: removeUnderscores(value),
      value: value,
    })),
  ];

  const pageTypeOptions: { label: string; value: PageTypeEnum }[] = [
    ...Object.entries(PageTypeEnum).map(([, value]) => ({
      label: unCamelcase(value),
      value: value,
    })),
  ];

  const currentPageTypeValues = localPageTypeFilter?.map((pageType) => pageType.value);

  const advertiserIds = localAdvertiserFilter?.map((adv) => adv.value);

  const campaignIds = localCampaignFilter?.map((campaign) => campaign.value);

  const lineitemIds = localLineItemFilter?.map((lineItem) => lineItem.value);

  const { data, error } = useQuery(GET_CAMPAIGN_IDS, {
    variables: {
      breakdownType: breakdownType,
      brand: localBrandFilter ? localBrandFilter.value : null,
      pageTypes: localPageTypeFilter? currentPageTypeValues : null,
      advertiserIds: advertiserIds ? advertiserIds : null,
      campaignIds: null,
      adGroupIds: localLineItemFilter ? lineitemIds : null,
      startDate: startDateString,
      endDate: endDateString,
      idType: TacticIdTypeEnum.CampaignId,
    },
    context: { clientName: "illumaDbEndpoint", type: "graphql" },
  });

  const campaignOptionsList = data?.getTacticIds.ids || [];

  const campaignOptions: { label: string; value: string }[] = [
    ...campaignOptionsList.map((campaign: string) => ({
      label: campaign,
      value: campaign,
    })),
  ];

  const { data: lineItemFilterData, error: lineItemFilterError } = useQuery(
    GET_CAMPAIGN_IDS,
    {
      variables: {
        brand: localBrandFilter ? localBrandFilter.value : null,
        breakdownType: breakdownType,
        pageTypes: localPageTypeFilter? currentPageTypeValues : null,
        advertiserIds: advertiserIds ? advertiserIds : null,
        campaignIds: localCampaignFilter ? campaignIds : null,
        adGroupIds: null,
        startDate: startDateString,
        endDate: endDateString,
        idType: TacticIdTypeEnum.AdGroupId,
      },
      context: { clientName: "illumaDbEndpoint", type: "graphql" },
    }
  );

  const lineItemOptionsList = lineItemFilterData?.getTacticIds.ids || [];

  const lineItemOptions: { label: string; value: string }[] = [
    ...lineItemOptionsList.map((lineItem: string) => ({
      label: lineItem,
      value: lineItem,
    })),
  ];

  const handleBreakdownTypeChange = (value: string) => {
    setBreakdownType(value as BreakdownTypeEnum);
  };

  return (
    <>
      <VStack width={"100%"} alignItems={"flex-start"}>
        <Box w={[null, null, "100%", "14%", null]} mt={3}>
          <ButtonGroup
            _hover={{ borderColor: "blueInputHover" }}
            border={"1px"}
            borderColor="greyInput"
            bg="greyInput"
            position="relative"
            borderRadius="lg"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top="0"
              left={
                breakdownType === BreakdownTypeEnum.Combined
                  ? "0"
                  : keywordSelectedSliderStart
              }
              width="calc(50% + 8px)"
              height="100%"
              bg="blueButton"
              borderRadius="md"
              transition="all 0.3s ease"
              zIndex="0"
              mr={3}
            />

            <Button
              size={"sm"}
              fontSize={["xs", null, null, "sm", null]}
              display={["none", null, "flex", null, null]}
              onClick={() =>
                handleBreakdownTypeChange(BreakdownTypeEnum.Combined)
              }
              fontWeight={"medium"}
              textColor={
                breakdownType === BreakdownTypeEnum.Combined ? "white" : "black"
              }
              bgColor="transparent"
              zIndex="1"
              borderRadius="md"
              _hover={{
                bg: "transparent",
              }}
            >
              Combined
            </Button>
            <Button
              size={"sm"}
              fontSize={["xs", null, null, "sm", null]}
              display={["flex", null, "flex", null, null]}
              onClick={() =>
                handleBreakdownTypeChange(BreakdownTypeEnum.Keyword)
              }
              fontWeight={"medium"}
              textColor={
                breakdownType === BreakdownTypeEnum.Keyword ? "white" : "black"
              }
              bgColor="transparent"
              zIndex="1"
              borderRadius="md"
              _hover={{
                bg: "transparent",
              }}
            >
              Keyword
            </Button>
          </ButtonGroup>
        </Box>

        <Flex justifyContent={"flex-end"} width={"100%"} mt={3}>
          {currentUser?.isStaff && (
            <Box w={[null, null, "100%", "27%", null]} mr={2} mt={2}>
              <SelectFieldV2
                styles={{
                  valueContainer: (base) => ({
                    ...base,
                    backgroundColor: "white",
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    backgroundColor: "white",
                    borderColor: "chakra.colors.theme.blueInputHover",
                    borderRadius: "3px",
                  }),
                  menu: (base) => ({
                    ...base,
                    borderColor: "chakra.colors.theme.blueInputHover",
                    borderWidth: "2px",
                    borderRadius: "chakraTheme.radii.xs",
                  }),
                }}
                placeholder={`Select Brand`}
                onChange={(option) => {
                  setLocalBrandFilter(option);
                }}
                options={brandOptions}
                value={localBrandFilter}
              />
            </Box>
          )}
          <Box w={[null, null, "100%", "27%", null]} mr={2} mt={2}>
            <SelectFieldV2
              styles={{
                valueContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                  borderColor: "chakra.colors.theme.blueInputHover",
                  borderRadius: "3px",
                }),
                menu: (base) => ({
                  ...base,
                  borderColor: "chakra.colors.theme.blueInputHover",
                  borderwidth: "2px",
                  borderRadius: "chakraTheme.radii.xs",
                }),
              }}
              placeholder={`Select Environment`}
              onChange={(option) => {
                if (option && option.value !== null) {
                  setCurrentPageTypeSelection(option);
                }
              }}
              onMenuClose={() => {
                setLocalPageTypeFilter(currentPageTypeSelection);
              }}
              isMulti={true}
              options={pageTypeOptions}
              value={currentPageTypeSelection}
            />
          </Box>

          <Box w={[null, null, "100%", "27%", null]} mr={2} mt={2}>
            <SelectFieldV2
              styles={{
                valueContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                  borderColor: "chakra.colors.theme.blueInputHover",
                  borderRadius: "3px",
                }),
                menu: (base) => ({
                  ...base,
                  borderColor: "chakra.colors.theme.blueInputHover",
                  borderwidth: "2px",
                  borderRadius: "chakraTheme.radii.xs",
                }),
              }}
              placeholder={`Select Advertiser`}
              onChange={(option) => {
                if (option && option.value !== null) {
                  setCurrentAdvertiserSelection(option);
                }
              }}
              onMenuClose={() => {
                setLocalAdvertiserFilter(currentAdvertiserSelection);
              }}
              isMulti={true}
              options={advertiserOptions}
              value={currentAdvertiserSelection}
            />
          </Box>

          <Box w={[null, null, "100%", "27%", null]} mr={2} mt={2}>
            <SelectFieldV2
              styles={{
                valueContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                  borderColor: "chakra.colors.theme.blueInputHover",
                  // width: "100%",
                  borderRadius: "3px",
                }),
                menu: (base) => ({
                  ...base,
                  borderColor: "chakra.colors.theme.blueInputHover",
                  borderwidth: "2px",
                  borderRadius: "chakraTheme.radii.xs",
                }),
              }}
              placeholder={`Select Campaign`}
              isDisabled={!!error}
              onChange={(option) => {
                if (option !== undefined) {
                  setCurrentCampaignIdSelection(option);
                }
              }}
              onMenuClose={() => {
                setLocalCampaignFilter(currentCampaignIdSelection);
              }}
              isMulti={true}
              options={campaignOptions}
              value={currentCampaignIdSelection}
            />
          </Box>

          <Box w={[null, null, "100%", "27%", null]} mr={2} mt={2}>
            <SelectFieldV2
              styles={{
                valueContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                  borderColor: "chakra.colors.theme.blueInputHover",
                  // width: "100%",
                  borderRadius: "3px",
                }),
                menu: (base) => ({
                  ...base,
                  borderColor: "chakra.colors.theme.blueInputHover",
                  borderwidth: "2px",
                  borderRadius: "chakraTheme.radii.xs",
                }),
              }}
              isDisabled={!!lineItemFilterError}
              placeholder={`Select Line Item`}
              onChange={(option) => {
                if (option !== undefined) {
                  setCurrentLineItemIdSelection(option);
                }
              }}
              onMenuClose={() => {
                setLocalLineItemFilter(currentLineItemIdSelection);
              }}
              isMulti={true}
              options={lineItemOptions}
              value={currentLineItemIdSelection}
            />
          </Box>
          <Box w={[null, null, "100%", "27%", null]} mr={2} mt={2}>
            <SelectFieldV2
              styles={{
                valueContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  backgroundColor: "white",
                  borderColor: "chakra.colors.theme.blueInputHover",
                  // width: "100%",
                  borderRadius: "3px",
                }),
                menu: (base) => ({
                  ...base,
                  borderColor: "chakra.colors.theme.blueInputHover",
                  borderwidth: "2px",
                  borderRadius: "chakraTheme.radii.xs",
                }),
              }}
              placeholder={`Select Range`}
              onChange={(option) => {
                setLocalDateRangeFilter(option);
              }}
              isMulti={false}
              options={lookbackOptions}
              value={localDateRangeFilter}
            />
          </Box>
        </Flex>
      </VStack>
    </>
  );
};

export const determineAdvertiserChoices = (
  brand: BrandChoicesOptionsType | undefined
) => {
  switch (brand?.value) {
    case BrandChoicesEnum.AppleTv:
      return [
        ...Object.entries(TempAppleAdvertiserChoicesEnum).map(([, value]) => ({
          label: getAppleAdvertiserName(value),
          value: value,
        })),
      ];

    case BrandChoicesEnum.Bayer:
      return [
        ...Object.entries(TempBayerAdvertiserChoicesEnum).map(([, value]) => ({
          label: getBayerAdvertiserName(value),
          value: value,
        })),
      ];
    case BrandChoicesEnum.Demo:
      return [
        ...Object.entries(TempDemoAdvertiserChoicesEnum).map(([, value]) => ({
          label: getDemoAdvertiserName(value),
          value: value,
        })),
      ];
    default:
      break;
  }
};
